import { useState, type ReactNode } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import { Link } from '@tanstack/react-router'
import {
  GitCommitHorizontal,
  CheckCircle,
  ChevronDown,
  Package,
  Play,
  Eye,
  ExternalLink,
  FileText,
  MessageSquare,
} from 'lucide-react'
import { Badge } from '@deepguide-ai/ui/badge'
import type { EvidenceScoreResult } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { formatEvidencePeriod } from '@/components/compliance/shared'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@deepguide-ai/ui/collapsible'
import { AIAssessmentSection } from '@/components/compliance/AIAssessmentSection'
import { ArtifactsSection } from '@/pages/(protected)/workspaces/[workspaceId]/compliance/tests/[testId]/components/ArtifactsSection'
import { cn } from '@/lib/utils'

interface SubmissionDetailsCardProps {
  submission: {
    id: string
    revisionNumber?: number | null
    reviewStatus?: string | null
    submittedAt?: Date | string | null
    submittedByUser?: {
      name?: string | null
      email?: string | null
      image?: string | null
      isServiceAccount?: boolean | null
    } | null
    submittedByEmail?: string | null
    evidenceFromDate?: Date | string | null
    evidenceToDate?: Date | string | null
    overallScore?: number | null
    scoreResult: EvidenceScoreResult
    qualityTier?: string | null
    comments?: string | null
    feedback?: string | null
    sourceExecutionId?: string | null
    executionIdsJson?: string[] | null
    evidencePack?: {
      id?: string | null
      storageUrl?: string | null
      replayVideoUrl?: string | null
    } | null
    docId?: string | null
    topicId?: string | null
    topicTitle?: string | null
    additionalFiles?: {
      id: string
      filename: string
      url: string
      mimeType: string
      size: number
    }[] | null
    reviewedAt?: Date | string | null
    elementScores?: {
      elementId: string
      elementName: string
      isPresent: boolean
      isValid: boolean
      confidence: number
      reasoningText: string
      detectedElements: string[]
      suggestions: string[]
      weightedScore: number
    }[] | null
  }
  /** Reviewer/assigned user info */
  reviewer?: {
    id: string
    name?: string | null
    email?: string | null
    image?: string | null
    isServiceAccount?: boolean | null
  } | null
  isActiveSubmission?: boolean
  workspaceId?: string
  /** Hide the "View Run" button (useful in sidebar) */
  hideViewRunButton?: boolean
  /** Compact mode for sidebar display */
  compact?: boolean
  className?: string
  /** Test info for display */
  testTitle?: string | null
  testCode?: string | null
  /** Test ID for linking to test page */
  testId?: string | null
  /** Optional panel-mode navigation override for opening the linked test. */
  onNavigateToTest?: (testId: string) => void
  /** Audit cycle context for linked auditor-center artifacts */
  auditCycleId?: string | null
  /** Show detailed AI scores (element scores). Reviewers see more detail than submitters. */
  showDetailedScores?: boolean
  /** Show the View button (useful in sidebar) */
  showViewButton?: boolean
  /** Render as an embedded section inside another card */
  embedded?: boolean
  /** Hide the header and top summary content when embedding in lineage details */
  hideOverview?: boolean
  /** Hide only the title row (title + status badges + view-run button) at the
   *  top of the overview block. Use when the page renders its own page-level
   *  header. Submitter row and reviewer info still render. */
  hideTitle?: boolean
  /** Hide the AI assessment section */
  hideAssessment?: boolean
  /** Hide the submitter metadata row */
  hideSubmitter?: boolean
  /** Hide the assets section */
  hideAssets?: boolean
  /** Hide the "Evidence Files" header row above the assets list. Use when
   *  the surrounding pane already labels this section. */
  hideAssetsHeader?: boolean
  /** Fold the evidence pack (its signed manifest) into the Evidence Files
   *  table as a regular row instead of a separate "Assets" collapsible —
   *  used by the auditor surfaces so the pack sits alongside the other
   *  artifacts (e.g. period-rollup.md). Replay video, when present, keeps
   *  the Assets section (it can't live in the files table). */
  mergePackIntoFiles?: boolean
  /** Hide the reviewer ("Approved by …") row inside the overview block. */
  hideReviewer?: boolean
  /** Hide the AI Review / feedback section. */
  hideFeedback?: boolean
  /** Optional action rendered in the header row */
  headerAction?: ReactNode
  /** Layout direction for asset rows */
  assetLayout?: 'horizontal' | 'vertical'
  /** Linked evidence documents from the test status (policies, etc.) */
  evidenceDocLinks?: {
    id: string
    documentType: string
    documentTitle: string
    documentId: string
    policySlug?: string | null
    versionId?: string | null
    viewUrl?: string | null
    downloadUrl?: string | null
    fileName?: string | null
    mimeType?: string | null
  }[]
  /** Custom label for feedback section (e.g. "AI Review"). When set, feedback shows even with hideOverview. */
  feedbackLabel?: string
}

export function SubmissionDetailsCard({
  submission,
  reviewer,
  isActiveSubmission = false,
  workspaceId,
  hideViewRunButton = false,
  compact = false,
  className,
  testTitle,
  testCode,
  testId,
  onNavigateToTest,
  auditCycleId,
  showDetailedScores = false,
  showViewButton = true,
  embedded = false,
  hideOverview = false,
  hideTitle = false,
  hideAssessment = false,
  hideSubmitter = false,
  hideAssets = false,
  hideAssetsHeader = false,
  mergePackIntoFiles = false,
  hideReviewer = false,
  hideFeedback = false,
  headerAction,
  assetLayout = 'horizontal',
  evidenceDocLinks = [],
  feedbackLabel,
}: SubmissionDetailsCardProps) {
  const [assetsOpen, setAssetsOpen] = useState(true)

  const userName =
    submission.submittedByUser?.name ||
    submission.submittedByUser?.email ||
    submission.submittedByEmail ||
    'Unknown'

  const isApproved = submission.reviewStatus === 'APPROVED'
  const executionId = submission.sourceExecutionId || submission.executionIdsJson?.[0] || ''

  const hasContent =
    (!hideOverview &&
      Boolean(
        testTitle ||
          (submission.evidenceFromDate && submission.evidenceToDate) ||
          submission.comments
      )) ||
    (!hideAssessment &&
      submission.overallScore !== null &&
      submission.overallScore !== undefined)

  return (
    <div className={cn(embedded ? 'border-0 rounded-none bg-card' : 'rounded-md border bg-card', className)}>
      {!hideOverview && (!hideTitle || !hideSubmitter || (!!reviewer && !hideReviewer)) && (
        <div className={cn('border-b ', embedded ? 'px-0 pt-0 pb-4' : compact ? 'p-4' : 'p-6')}>
          {/* Title row */}
          {!hideTitle && (
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2 flex-wrap'>
                <h3 className={cn('font-bold', compact ? 'text-lg' : 'text-2xl')}>
                  Submission #{submission.revisionNumber}
                </h3>
                {isActiveSubmission && (
                  <Badge className='bg-status-green-bg text-status-green-strong border-status-green/30'>
                    Latest
                  </Badge>
                )}
                {submission.reviewStatus === 'PENDING' && (
                  isActiveSubmission ? (
                    <Badge className='bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'>
                      Awaiting Review
                    </Badge>
                  ) : (
                    <Badge variant='outline' className='text-muted-foreground'>
                      Superseded
                    </Badge>
                  )
                )}
                {submission.reviewStatus === 'NEEDS_REVISION' && (
                  <Badge className='bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'>
                    Needs Revision
                  </Badge>
                )}
                {isApproved && (
                  <Badge className='bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'>
                    Approved
                  </Badge>
                )}
                {submission.reviewStatus === 'WITHDRAWN' && (
                  <Badge variant='outline' className='text-muted-foreground'>
                    Withdrawn
                  </Badge>
                )}
              </div>
              <div className='flex items-center gap-2'>
                {headerAction}
                {!hideViewRunButton && workspaceId && executionId && (
                  <Link
                    to='/workspaces/$workspaceId/runs/$executionId'
                    params={{
                      workspaceId,
                      executionId,
                    }}
                  >
                    <Button variant='outline' size='sm' className='gap-1'>
                      View Run
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Metadata row */}
          {!hideSubmitter && (
            <div className={cn(
              'flex items-center gap-3 mt-2 text-sm text-muted-foreground flex-wrap',
              compact && 'text-xs'
            )}>
              <div className='flex items-center gap-1.5'>
                <MemberAvatar
                  person={{
                    image: submission.submittedByUser?.image,
                    name: submission.submittedByUser?.name,
                    email: submission.submittedByUser?.email || submission.submittedByEmail,
                    isServiceAccount: submission.submittedByUser?.isServiceAccount,
                  }}
                  size='sm'
                />
                <span className='font-medium text-foreground'>
                  {userName}
                  {submission.submittedByUser?.isServiceAccount && (
                    <span className='ml-1 font-normal text-text-placeholder'>(AI)</span>
                  )}
                </span>
              </div>
              <span>
                {submission.submittedAt
                  ? formatDistanceToNow(new Date(submission.submittedAt), { addSuffix: false }) + ' ago'
                  : ''}
              </span>
              <div className='flex items-center gap-1'>
                <GitCommitHorizontal className='h-3.5 w-3.5' />
                <span className='font-mono'>{submission.id.slice(0, 7)}</span>
                {isApproved && <CheckCircle className='h-3.5 w-3.5 text-status-green ml-1' />}
              </div>
            </div>
          )}

          {/* Reviewer info */}
          {reviewer && !hideReviewer && (
            <div className={cn(
              'flex items-center gap-2 mt-2 text-sm',
              compact && 'text-xs'
            )}>
              <span className='text-muted-foreground'>
                {submission.reviewStatus === 'PENDING'
                  ? 'Reviewer:'
                  : submission.reviewStatus === 'APPROVED'
                    ? 'Approved by:'
                    : 'Reviewed by:'}
              </span>
              <div className='flex items-center gap-1.5'>
                <MemberAvatar
                  person={{
                    image: reviewer.image,
                    name: reviewer.name,
                    email: reviewer.email,
                    isServiceAccount: reviewer.isServiceAccount,
                  }}
                  size='sm'
                />
                <span className='font-medium text-foreground'>
                  {reviewer.name || reviewer.email || 'Unknown'}
                  {reviewer.isServiceAccount && (
                    <span className='ml-1 font-normal text-text-placeholder'>(AI)</span>
                  )}
                </span>
              </div>
              {submission.reviewedAt && (submission.reviewStatus === 'APPROVED' || submission.reviewStatus === 'NEEDS_REVISION') && (
                <span className='text-muted-foreground'>
                  · {format(new Date(submission.reviewedAt), 'MMM d, yyyy h:mm:ss a')}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {/* AI Review / Feedback — shown inline when feedbackLabel is set, even with hideOverview */}
      {feedbackLabel && submission.feedback && !hideFeedback && (
        <div className={cn('border-b', compact ? 'px-4 py-3' : 'px-6 py-4')}>
          <div className='mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-text-subtle'>
            <MessageSquare className='h-3.5 w-3.5' />
            {feedbackLabel}
          </div>
          <p className='text-[13px] leading-[1.6] text-text-body'>{submission.feedback}</p>
        </div>
      )}
      {/* Content section — skip entirely when overview is hidden and no assessment */}
      {hasContent && !(hideOverview && (hideAssessment || submission.overallScore == null)) && <div
        className={cn(
          !hideOverview && 'border-b',
          embedded ? 'px-0 py-4 space-y-4' : compact ? 'p-4 space-y-3' : 'p-6 space-y-4',
        )}
      >
        {/* Test info */}
        {!hideOverview && testTitle && (
          <div>
            <h4 className={cn('font-semibold mb-1', compact && 'text-sm')}>Test</h4>
            <div className='flex items-center gap-2 text-sm'>
              <span>
                {testTitle}
                {testCode && (
                  <span className='text-muted-foreground ml-2'>({testCode})</span>
                )}
              </span>
              {testId && workspaceId && (
                onNavigateToTest ? (
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='h-6 w-6'
                    onClick={() => onNavigateToTest(testId)}
                  >
                    <ExternalLink className='h-3.5 w-3.5' />
                  </Button>
                ) : (
                  <Link
                    to='/workspaces/$workspaceId/tests/$testId'
                    params={{ workspaceId, testId }}
                  >
                  <Button variant='ghost' size='icon' className='h-6 w-6'>
                    <ExternalLink className='h-3.5 w-3.5' />
                  </Button>
                  </Link>
                )
              )}
            </div>
          </div>
        )}

        {/* Evidence period if available */}
        {!hideOverview && submission.evidenceFromDate && submission.evidenceToDate && (
          <div>
            <h4 className={cn('font-semibold mb-1', compact && 'text-sm')}>Evidence Period</h4>
            <p className='text-sm text-muted-foreground'>
              {formatEvidencePeriod(submission.evidenceFromDate, submission.evidenceToDate)}
            </p>
          </div>
        )}

        {/* Submitter Notes */}
        {!hideOverview && submission.comments && (
          <div>
            <h4 className={cn('font-semibold mb-1', compact && 'text-sm')}>Submitter Notes</h4>
            <p className='text-sm'>{submission.comments}</p>
          </div>
        )}


        {/* AI Assessment - Score, Feedback, Element Scores */}
        {!hideAssessment && submission.overallScore !== null && submission.overallScore !== undefined && (
          <AIAssessmentSection
            overallScore={submission.overallScore}
            scoreResult={submission.scoreResult}
            qualityTier={submission.qualityTier}
            feedback={submission.feedback}
            elementScores={submission.elementScores?.map(e => ({
              elementId: e.elementId,
              elementName: e.elementName,
              isValid: e.isValid,
              reasoningText: e.reasoningText,
            }))}
            showElementScores={showDetailedScores}
          />
        )}

        {/* Empty state if no content */}
        {!hideOverview && !testTitle && !submission.overallScore && !submission.comments && !submission.feedback && !submission.evidenceFromDate && !submission.docId && !(submission.additionalFiles && submission.additionalFiles.length > 0) && !(evidenceDocLinks && evidenceDocLinks.length > 0) && (
          <p className='text-sm text-muted-foreground'>No additional details available.</p>
        )}
      </div>}
      {/* Assets section - collapsible. When the pack is merged into the
          files table (auditor surfaces), this section only remains for the
          replay video. */}
      {!hideAssets && submission.evidencePack && ((submission.evidencePack.storageUrl && !mergePackIntoFiles) || submission.evidencePack.replayVideoUrl) && (
        <Collapsible open={assetsOpen} onOpenChange={setAssetsOpen}>
          <CollapsibleTrigger className={cn(
            'flex items-center gap-2 w-full hover:bg-muted/50 transition-colors',
            embedded ? 'px-0 py-3' : compact ? 'p-3' : 'p-4'
          )}>
            <ChevronDown className={cn('h-4 w-4 transition-transform', !assetsOpen && '-rotate-90')} />
            <span className='font-semibold text-sm'>Assets</span>
            <Badge variant='secondary' className='text-xs'>
              {(submission.evidencePack.storageUrl && !mergePackIntoFiles ? 1 : 0) + (submission.evidencePack.replayVideoUrl ? 1 : 0)}
            </Badge>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className='divide-y'>
              {/* Evidence Pack download */}
              {submission.evidencePack.storageUrl && !mergePackIntoFiles && (
                <div className={cn(
                  'hover:bg-muted/30',
                  assetLayout === 'vertical'
                    ? 'flex flex-col items-start gap-2'
                    : 'flex items-center justify-between',
                  embedded ? 'px-0 py-2.5' : compact ? 'px-4 py-2' : 'px-6 py-3'
                )}>
                  <div className='flex items-center gap-2'>
                    <Package className='h-4 w-4 text-muted-foreground' />
                    <a
                      href={submission.evidencePack.storageUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-sm text-cobalt-strong hover:underline dark:text-blue-400'
                    >
                      Evidence Pack (zip)
                    </a>
                  </div>
                  {/* View button - links to Evidence Pack Viewer */}
                  {showViewButton && workspaceId && submission.evidencePack.id && (
                    <Link
                      to='/workspaces/$workspaceId/submissions/$submissionId'
                      params={{
                        workspaceId,
                        submissionId: submission.id || '',
                      }}
                      className={cn(assetLayout === 'vertical' && 'w-full')}
                    >
                      <Button
                        variant='ghost'
                        size='sm'
                        className={cn('gap-1 h-7 text-xs', assetLayout === 'vertical' && 'justify-start px-0')}
                      >
                        <Eye className='h-3 w-3' />
                        View
                      </Button>
                    </Link>
                  )}
                </div>
              )}
              {/* Replay video */}
              {submission.evidencePack.replayVideoUrl && (
                <div className={cn(
                  'hover:bg-muted/30',
                  assetLayout === 'vertical'
                    ? 'flex flex-col items-start gap-2'
                    : 'flex items-center justify-between',
                  embedded ? 'px-0 py-2.5' : compact ? 'px-4 py-2' : 'px-6 py-3'
                )}>
                  <div className='flex items-center gap-2'>
                    <Play className='h-4 w-4 text-muted-foreground' />
                    <a
                      href={submission.evidencePack.replayVideoUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-sm text-cobalt-strong hover:underline dark:text-blue-400'
                    >
                      Replay Video (mp4)
                    </a>
                  </div>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
      {/* Evidence Details — linked documents, submission doc, uploaded files.
          Rendered INDEPENDENTLY of the evidence pack: pack-bearing submissions
          (e.g. RFC-208 period rollups) carry their real evidence in
          additionalFiles (period-rollup.md, period-rollup-runs.json, run
          artifacts) — hiding those behind "has a pack" left reviewers and
          auditors with only an opaque zip link. */}
      {!hideAssets && (() => {
        const hasPack = !!(submission.evidencePack?.storageUrl || submission.evidencePack?.replayVideoUrl)
        // Merged mode: the pack's signed manifest becomes a regular row in
        // the files table, next to the other artifacts.
        const packFileEntry =
          mergePackIntoFiles && submission.evidencePack?.storageUrl
            ? {
                id: `evidence-pack-${submission.evidencePack.id ?? 'manifest'}`,
                filename: 'evidence-pack-manifest.json',
                url: submission.evidencePack.storageUrl,
                mimeType: 'application/json',
                size: 0,
                uploadedAt: submission.submittedAt,
              }
            : null
        const hasDocLinks = evidenceDocLinks.length > 0
        const hasSubmissionDoc = !!submission.docId
        const hasFiles =
          (submission.additionalFiles?.length ?? 0) > 0 || Boolean(packFileEntry)
        const hasEvidenceDetails = hasDocLinks || hasSubmissionDoc || hasFiles

        if (!hasEvidenceDetails) {
          // The pack Assets section above already covers the pack-only case.
          if (hasPack) return null
          return (
            <div className={cn('text-center text-muted-foreground', embedded ? 'px-0 py-4' : compact ? 'p-4' : 'p-6')}>
              <Package className={cn('mx-auto mb-2 opacity-50', compact ? 'h-6 w-6' : 'h-8 w-8')} />
              <p className='text-sm'>No evidence snapshot available for this submission</p>
            </div>
          )
        }

        return (
          <div className={cn(embedded ? 'px-0 py-3' : compact ? 'p-4' : 'p-4')}>
            <ArtifactsSection
              testType='WORKFLOW'
              displayTemplateType='GUIDED'
              filesData={[]}
              latestRunbookArtifacts={[]}
              subtaskStats={null}
              collectorSources={[]}
              showCollectorSourcesSection={false}
              policySlug={undefined}
              isApproved={true}
              workspaceId={workspaceId ?? ''}
              testId={testId ?? ''}
              statusId={testId ?? ''}
              auditCycleId={auditCycleId ?? undefined}
              snapshot={{
                files: [
                  ...(packFileEntry ? [packFileEntry] : []),
                  ...(submission.additionalFiles ?? []),
                ].map((file) => ({ ...file, uploadedAt: new Date(file.uploadedAt).toISOString() })),
                evidenceDocLinks: evidenceDocLinks.map((link) => ({
                  id: link.id,
                  documentType: link.documentType,
                  documentTitle: link.documentTitle,
                  documentId: link.documentId,
                  policySlug: link.policySlug,
                  versionId: link.versionId,
                  viewUrl: link.viewUrl ?? null,
                  downloadUrl: link.downloadUrl ?? null,
                  fileName: link.fileName ?? null,
                  mimeType: link.mimeType ?? null,
                })),
              }}
              sectionLabel='Evidence Files'
              hideSectionLabel={hideAssetsHeader}
              unstyled={false}
            />
          </div>
        )
      })()}
    </div>
  );
}
